import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import { lightTheme } from "./Palette/light-palette";
import { darkTheme } from "./Palette/dark-palette";

export type ColorMode = "light" | "dark";

export const COLOR_MODE_STORAGE_KEY = "tb_color_mode";

type ColorModeContextValue = {
  mode: ColorMode;
  toggleColorMode: () => void;
  setColorMode: (mode: ColorMode) => void;
};

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: "light",
  toggleColorMode: () => undefined,
  setColorMode: () => undefined,
});

export function getStoredColorMode(): ColorMode {
  try {
    const saved = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (saved === "dark" || saved === "light") {
      return saved;
    }
  } catch {
    // ignore storage errors
  }
  return "light";
}

export function applyColorMode(mode: ColorMode) {
  document.documentElement.setAttribute("data-theme", mode);
  document.documentElement.style.colorScheme = mode;
  try {
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  } catch {
    // ignore storage errors
  }
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) {
    themeMeta.setAttribute("content", mode === "dark" ? "#0B0F14" : "#ffffff");
  }
}

export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ColorMode>(() => {
    const initial = getStoredColorMode();
    applyColorMode(initial);
    return initial;
  });

  useEffect(() => {
    applyColorMode(mode);
  }, [mode]);

  const setColorMode = useCallback((nextMode: ColorMode) => {
    setMode(nextMode);
  }, []);

  const toggleColorMode = useCallback(() => {
    setMode((current) => (current === "light" ? "dark" : "light"));
  }, []);

  const value = useMemo(
    () => ({
      mode,
      toggleColorMode,
      setColorMode,
    }),
    [mode, toggleColorMode, setColorMode]
  );

  const theme = mode === "dark" ? darkTheme : lightTheme;

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode() {
  return useContext(ColorModeContext);
}
